import { Elements } from '@stripe/react-stripe-js'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router-dom'

import routes from 'app/routes'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { STRIPE_PK } from 'app/consts'
import type { AuthorizedUser } from 'app/types'

import elementsLocaleMapper from 'app/utils/elementsLocaleMapper'
import { LocalizationContext } from 'components/i18n/LocalizationWrapper/LocalizationWrapper'
import { WhiteBlock } from 'components/Layout'
import { currentUserSelector } from 'features/auth/login/selectors'
import AddPaymentMethodForm from '../AddPaymentMethodForm'
import { paymentMethodsMessages } from '../AddPaymentMethodForm/messages'
import { StyledAddCardWrapper, StyledAddPaymentHeading } from './styles'
import { useHomeRouteParams } from 'features/home/hooks'
import { totalToPayAmountSelector } from '../../selectors'
import { subscriptionManagementMessages } from '../messages'
import { createSetupIntentThunk } from '../../slice'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import { getPaymentMethodsListThunk } from 'features/paymentMethods/slice'
import { hasNoPaymentMethodsSelector } from 'features/paymentMethods/selectors'
import SubsManageBottomPanel from 'components/SubsManageBottomPanel'

function AddCard() {
  const localizationContext = useContext(LocalizationContext)
  const { formatMessage } = useIntl()
  const history = useHistory()
  const dispatch = useAppDispatch()
  const { homeId } = useHomeRouteParams()

  const currentRootId = useAppSelector(currentRootDomainIdSelector)
  const user: AuthorizedUser = useAppSelector(currentUserSelector)
  const totalToPayAmount = useAppSelector(
    totalToPayAmountSelector(formatMessage)
  )

  const handleAddFirstMethod = useCallback(async () => {
    await dispatch(getPaymentMethodsListThunk(currentRootId || ''))
    if (currentRootId) {
      const subscriptionUpgradePath = generatePath(
        routes.homeSubscription.upgrade(),
        {
          homeId,
          rootDomainId: currentRootId,
        }
      )
      history.push(subscriptionUpgradePath)
    }
  }, [currentRootId, dispatch, history, homeId])

  const stripePromise = useMemo(
    () =>
      loadStripe(STRIPE_PK || '', {
        locale: elementsLocaleMapper(localizationContext.locale),
      }),
    [localizationContext.locale]
  )

  useEffect(() => {
    async function fetchStripeIntent() {
      await dispatch(createSetupIntentThunk(currentRootId || ''))
    }
    fetchStripeIntent()
  }, [currentRootId, dispatch, user.id])

  const hasNoPaymentMethods = useAppSelector(hasNoPaymentMethodsSelector)

  const handleCancel = useCallback(() => {
    if (currentRootId) {
      if (hasNoPaymentMethods) {
        const subscriptionPath = generatePath(routes.homeSubscription.root(), {
          homeId,
          rootDomainId: currentRootId || '',
        })
        history.push(subscriptionPath)
      } else {
        const subscriptionUpgradePath = generatePath(
          routes.homeSubscription.upgrade(),
          {
            homeId,
            rootDomainId: currentRootId,
          }
        )
        history.push(subscriptionUpgradePath)
      }
    }
  }, [currentRootId, history, homeId, hasNoPaymentMethods])

  return (
    <>
      <StyledAddCardWrapper>
        <WhiteBlock>
          <StyledAddPaymentHeading>
            {formatMessage(paymentMethodsMessages.formTitle)}
          </StyledAddPaymentHeading>
          <Elements key={localizationContext.locale} stripe={stripePromise}>
            <AddPaymentMethodForm
              isFirstMethod={hasNoPaymentMethods}
              onSuccess={handleAddFirstMethod}
            >
              {({ isSubmitDisabled }: { isSubmitDisabled: boolean }) => (
                <>
                  <SubsManageBottomPanel
                    isSubmitDisabled={isSubmitDisabled}
                    totalToPayAmount={totalToPayAmount}
                    submitLabel={formatMessage(
                      subscriptionManagementMessages.addPaymentMethodSubmit
                    )}
                    handleSubmit={() => {}}
                    handleCancel={handleCancel}
                  />
                </>
              )}
            </AddPaymentMethodForm>
          </Elements>
        </WhiteBlock>
      </StyledAddCardWrapper>
    </>
  )
}

export default AddCard
